import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import BannerHeader from './components/BannerHeader'
import Header from './components/Header'
import ProductCard from './components/regular/ProductCard'
import FashionProductCard from './components/fashion/FashionProductCard'
import MagazineProductSpread from './components/magazine/MagazineProductSpread'
import LinearCatalog from './components/linear/LinearCatalog'
import Footer from './components/Footer'
import FloatingActionButton from './components/FloatingActionButton'
import CartModal from './components/CartModal'
import CategoryNav from './components/CategoryNav'
import useHttp from '../../hooks/http.hook'
import { mapBusinessCategories, mapBusinessProductGroups } from '../../utils/productGroups'
import { useTheme } from '../../context/theme.context'
import { BusinessViewTypes, businessViewById } from '../../types/BusinessViewTypes'
import { BusinessTypes } from '../../types/BusinessType'
import RentalPeriodPicker from './components/RentalPeriodPicker'
import { rentalLineKey, uniquePeriodSuggestions } from '../../utils/rentalPeriod'
import './CustomerSide.css'

const CATALOG_PAGE_SIZE = 20

function catalogPageUrl(business, resource, page, categoryId) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(CATALOG_PAGE_SIZE),
  })
  if (categoryId != null && categoryId !== '') params.set('categoryId', String(categoryId))
  return `/business/${encodeURIComponent(business)}/${resource}?${params.toString()}`
}

function pageHasMore(pagination) {
  const page = Number(pagination?.page) || 1
  const totalPages = Number(pagination?.totalPages) || 0
  return page < totalPages
}

function appendUniqueById(prev, next) {
  const seen = new Set((prev || []).map((item) => String(item?.id ?? item?._id ?? '')))
  const out = [...(prev || [])]
  for (const item of next || []) {
    const key = String(item?.id ?? item?._id ?? '')
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(item)
  }
  return out
}

function cartStorageKey(business) {
  return business ? `shop-cart-v1-${business}` : null
}

function loadCartFromSession(business) {
  if (!business || typeof window === 'undefined') return {}
  try {
    const raw = sessionStorage.getItem(cartStorageKey(business))
    if (!raw) return {}
    const o = JSON.parse(raw)
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return {}
    return o
  } catch {
    return {}
  }
}

function parsePriceVal(value) {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : NaN
}

/** Plain discount from business API: promotionId + oldPrice + lower price */
function productHasDiscountedPrice(product) {
  const pid = product?.promotionId
  if (pid == null || pid === '') return false
  const cur = parsePriceVal(product?.price)
  const old = parsePriceVal(
    product?.oldPrice ?? product?.compareAtPrice ?? product?.compare_at_price ?? product?.listPrice
  )
  return Number.isFinite(cur) && Number.isFinite(old) && old > cur
}

function businessBannersFromBusiness(businessData) {
  const raw = businessData?.banners
  if (!Array.isArray(raw)) return []
  return raw
    .map((item, i) => {
      if (typeof item === 'string') {
        const url = item.trim()
        return url ? { url, index: i } : null
      }
      const url = String(item?.url ?? item?.imageUrl ?? '').trim()
      if (!url) return null
      const index = Number.parseInt(String(item?.index), 10)
      return { url, index: Number.isFinite(index) ? index : i }
    })
    .filter(Boolean)
    .sort((a, b) => a.index - b.index)
}

const CATALOG_SKELETON_COUNT = 8
const CATALOG_SKELETON_TITLE_WIDTHS = ['72%', '58%', '66%', '50%', '64%', '54%', '70%', '46%']

function CatalogSkeletons() {
  return (
    <div className="catalog-skeletons" role="status" aria-live="polite" aria-label="Загрузка">
      <div className="products-container" aria-hidden="true">
        {Array.from({ length: CATALOG_SKELETON_COUNT }, (_, index) => (
          <div key={index} className="product-card product-card--skeleton">
            <div className="product-image-container">
              <div className="skeleton skeleton--block product-skeleton-image" />
            </div>
            <div className="product-info">
              <div
                className="skeleton skeleton--text product-skeleton-title"
                style={{ width: CATALOG_SKELETON_TITLE_WIDTHS[index] }}
              />
              <div className="product-price-container">
                <div className="skeleton skeleton--text product-skeleton-price" />
                <div className="skeleton skeleton--circle product-skeleton-btn" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function stickyCategoryOffset() {
  const nav = document.querySelector('.category-nav-mobile-wrap')
  if (!nav) return 0
  const style = window.getComputedStyle(nav)
  if (style.display === 'none' || style.visibility === 'hidden') return 0
  return nav.getBoundingClientRect().height
}

function alignElementToViewportTop(element) {
  const offset = stickyCategoryOffset() + 20
  element.style.scrollMarginTop = `${offset}px`
  const scroller = document.scrollingElement || document.documentElement
  const y = Math.max(0, element.getBoundingClientRect().top + scroller.scrollTop - offset)
  const html = document.documentElement
  const previous = html.style.scrollBehavior
  html.style.scrollBehavior = 'auto'
  scroller.scrollTop = y
  document.body.scrollTop = y
  window.scrollTo(0, y)
  element.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' })
  html.style.scrollBehavior = previous
}

function CatalogProducts({ products, categoryName, cartCount, onAddToCart, titleRef, CardComponent = ProductCard }) {
  const pinned = categoryName ? products.filter((product) => product.pinned) : []
  const rest = categoryName ? products.filter((product) => !product.pinned) : products
  const renderCard = (product) => (
    <CardComponent
      key={product.id}
      product={product}
      cartCount={cartCount}
      onAddToCart={onAddToCart}
    />
  )

  return (
    <>
      {pinned.length > 0 ? (
        <section className="catalog-category-block" aria-label={categoryName}>
          <h2 className="catalog-category-title" ref={titleRef}>{categoryName}</h2>
          <div className="products-container">{pinned.map(renderCard)}</div>
        </section>
      ) : null}
      {pinned.length > 0 && rest.length > 0 ? (
        <hr className="catalog-category-rule" />
      ) : null}
      {rest.length > 0 ? <div className="products-container">{rest.map(renderCard)}</div> : null}
    </>
  )
}

function CustomerSide() {
  const { business } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { requestWithMeta } = useHttp()
  const { applyBusinessColorType } = useTheme()
  const giftMergeSigRef = useRef(null)
  const [cart, setCart] = useState(() => loadCartFromSession(business))
  const [fabVisible, setFabVisible] = useState(false)
  const [fabPosition, setFabPosition] = useState(null)
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [activeCategoryId, setActiveCategoryId] = useState(null)
  const [categoriesHasMore, setCategoriesHasMore] = useState(false)
  const [categoryTotal, setCategoryTotal] = useState(0)
  const [productsHasMore, setProductsHasMore] = useState(false)
  const [productsPending, setProductsPending] = useState(false)
  const [navStuck, setNavStuck] = useState(false)
  const [businessData, setBusinessData] = useState(null)
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [rentalPicker, setRentalPicker] = useState(null)
  const [rentalNotice, setRentalNotice] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const navSentinelRef = useRef(null)
  const productsEndRef = useRef(null)
  const selectedCategoryRef = useRef(null)
  const productsRequestRef = useRef(0)
  const productsPageRef = useRef(1)
  const productsHasMoreRef = useRef(false)
  const productsLoadingRef = useRef(false)
  const categoriesPageRef = useRef(1)
  const categoriesHasMoreRef = useRef(false)
  const categoriesLoadingRef = useRef(false)
  const catalogBodyRef = useRef(null)
  const categoryTitleRef = useRef(null)
  const scrollCategoryIntoViewRef = useRef(false)
  const categoryScrollSawPendingRef = useRef(false)
  const productsActivatedRef = useRef(false)

  const logoUrl = businessData?.logoUrl ?? null
  const banners = useMemo(() => businessBannersFromBusiness(businessData), [businessData])
  const businessView = businessViewById(businessData?.viewType).id
  const isFashionView = businessView === BusinessViewTypes.FASHION
  const isMagazineView = businessView === BusinessViewTypes.MAGAZINE
  const isLinearView = businessView === BusinessViewTypes.LINEAR
  const businessViewClass = isFashionView
    ? 'fashion'
    : isMagazineView
      ? 'magazine'
      : isLinearView
        ? 'linear'
        : 'regular'
  const showCategoryNav = categoryTotal > 1 && !isMagazineView

  // Global `index.css` locks scrolling + root height for Telegram/provider UX.
  // Customer side should scroll normally with non-sticky header/footer.
  useEffect(() => {
    const html = document.documentElement
    const prevHtmlOverflow = html.style.overflow
    const prevHtmlHeight = html.style.height
    const prevBodyOverflow = document.body.style.overflow
    const prevBodyHeight = document.body.style.height
    const root = document.getElementById('root')
    const prevRootOverflow = root?.style.overflow
    const prevRootHeight = root?.style.height
    const prevRootMinHeight = root?.style.minHeight

    html.style.overflow = 'auto'
    html.style.height = 'auto'
    html.style.scrollBehavior = 'smooth'
    document.body.style.overflow = 'visible'
    document.body.style.height = 'auto'
    if (root) {
      root.style.overflow = 'visible'
      root.style.height = 'auto'
      root.style.minHeight = '100vh'
    }

    return () => {
      html.style.overflow = prevHtmlOverflow
      html.style.height = prevHtmlHeight
      html.style.scrollBehavior = ''
      document.body.style.overflow = prevBodyOverflow
      document.body.style.height = prevBodyHeight
      if (root) {
        root.style.overflow = prevRootOverflow || ''
        root.style.height = prevRootHeight || ''
        root.style.minHeight = prevRootMinHeight || ''
      }
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const fetchBusinessData = async () => {
      if (!business) {
        setError('Business name is required')
        setLoading(false)
        return
      }

      selectedCategoryRef.current = null
      setActiveCategoryId(null)
      productsActivatedRef.current = false
      categoriesPageRef.current = 1
      categoriesHasMoreRef.current = false
      categoriesLoadingRef.current = false
      setCategoriesHasMore(false)
      setCategoryTotal(0)
      productsRequestRef.current += 1
      const requestId = productsRequestRef.current
      productsPageRef.current = 1
      productsHasMoreRef.current = false
      productsLoadingRef.current = true
      setProductsHasMore(false)
      setProductsPending(false)

      try {
        setLoading(true)
        setError(null)
        const qs = new URLSearchParams(location.search)
        const transitionPlatform = qs.get('transitionPlatform')
        const businessPath = `/business/${encodeURIComponent(business)}`
        const businessUrl =
          transitionPlatform && transitionPlatform.trim()
            ? `${businessPath}?transitionPlatform=${encodeURIComponent(transitionPlatform.trim())}`
            : businessPath
        const [businessRes, categoryRes, productRes] = await Promise.all([
          requestWithMeta(businessUrl),
          requestWithMeta(catalogPageUrl(business, 'categories', 1)),
          requestWithMeta(catalogPageUrl(business, 'products', 1)),
        ])
        if (cancelled || requestId !== productsRequestRef.current) return
        if (!businessRes.ok) {
          if (businessRes.status === 404) setError('Business not found')
          else setError(businessRes.data?.message || businessRes.data?.error || 'Failed to load business data')
          return
        }
        if (!categoryRes.ok || !productRes.ok) {
          const failed = !categoryRes.ok ? categoryRes : productRes
          if (failed.status === 404) setError('Business not found')
          else setError(failed.data?.message || failed.data?.error || 'Failed to load business data')
          return
        }

        setBusinessData(businessRes.data.business)
        applyBusinessColorType(business, businessRes.data.business?.colorType)
        setCategories(mapBusinessCategories(categoryRes.data?.categories))
        categoriesHasMoreRef.current = pageHasMore(categoryRes.data?.pagination)
        setCategoriesHasMore(categoriesHasMoreRef.current)
        setCategoryTotal(Number(categoryRes.data?.pagination?.total) || 0)
        setProducts(mapBusinessProductGroups(productRes.data?.products))
        productsHasMoreRef.current = pageHasMore(productRes.data?.pagination)
        setProductsHasMore(productsHasMoreRef.current)
      } catch (err) {
        if (!cancelled) setError('Failed to fetch business data: ' + err.message)
      } finally {
        if (requestId === productsRequestRef.current) {
          productsLoadingRef.current = false
          productsActivatedRef.current = true
        }
        if (!cancelled) setLoading(false)
      }
    }

    fetchBusinessData()
    return () => {
      cancelled = true
    }
  }, [business, location.search, requestWithMeta, applyBusinessColorType])

  useEffect(() => {
    if (!business) return
    const clearing = activeCategoryId == null
    if (clearing) {
      if (selectedCategoryRef.current != null || !productsActivatedRef.current) return
    } else if (
      selectedCategoryRef.current == null
      || String(selectedCategoryRef.current) !== String(activeCategoryId)
    ) {
      return
    }
    let cancelled = false
    const requestId = ++productsRequestRef.current
    productsPageRef.current = 1
    productsHasMoreRef.current = false
    productsLoadingRef.current = true
    const catalogBody = catalogBodyRef.current
    if (catalogBody) catalogBody.style.minHeight = `${catalogBody.getBoundingClientRect().height}px`
    setProducts([])
    setProductsHasMore(false)
    setProductsPending(true)

    const loadCategoryProducts = async () => {
      try {
        const { ok, status, data } = await requestWithMeta(
          catalogPageUrl(business, 'products', 1, clearing ? null : activeCategoryId)
        )
        if (cancelled || requestId !== productsRequestRef.current) return
        if (!ok) {
          if (status === 404) setError('Business not found')
          else setError(data?.message || data?.error || 'Failed to load business data')
          return
        }
        setProducts(mapBusinessProductGroups(data?.products))
        productsHasMoreRef.current = pageHasMore(data?.pagination)
        setProductsHasMore(productsHasMoreRef.current)
      } catch (err) {
        if (!cancelled && requestId === productsRequestRef.current) {
          setError('Failed to fetch business data: ' + err.message)
        }
      } finally {
        if (requestId === productsRequestRef.current) {
          productsLoadingRef.current = false
          if (catalogBodyRef.current) catalogBodyRef.current.style.minHeight = ''
          if (!cancelled) setProductsPending(false)
        }
      }
    }

    loadCategoryProducts()
    return () => {
      cancelled = true
    }
  }, [business, activeCategoryId, requestWithMeta])

  useEffect(() => {
    if (!businessData) return

    const businessName = String(businessData?.name || business || '').trim()
    if (businessName) document.title = businessName

    const logoUrl = businessData?.logoUrl
    if (!logoUrl) return

    const href = String(logoUrl).trim()
    if (!href) return

    // Prefer existing icons; otherwise create one.
    const existing =
      document.querySelector('link[rel="icon"]') ||
      document.querySelector('link[rel="shortcut icon"]') ||
      document.querySelector('link[rel~="icon"]')

    const link = existing || document.createElement('link')
    link.setAttribute('rel', 'icon')
    link.setAttribute('href', href)
    if (!existing) document.head.appendChild(link)
  }, [business, businessData])

  useEffect(() => {
    if (!business) return
    const next = loadCartFromSession(business)
    setCart(next)
    setFabVisible(Object.keys(next).length > 0)
  }, [business])

  useEffect(() => {
    if (!business) return
    try {
      sessionStorage.setItem(cartStorageKey(business), JSON.stringify(cart))
    } catch (_) {}
  }, [business, cart])

  // Detect when sticky category nav is stuck at the top (show compact logo).
  useEffect(() => {
    if (!showCategoryNav || loading) {
      setNavStuck(false)
      return
    }

    const sentinel = navSentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setNavStuck(!entry.isIntersecting)
      },
      { threshold: 0 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [showCategoryNav, loading, categories.length])

  const loadMoreCategories = useCallback(async () => {
    if (!business || categoriesLoadingRef.current || !categoriesHasMoreRef.current) return
    categoriesLoadingRef.current = true
    const nextPage = categoriesPageRef.current + 1
    try {
      const { ok, data } = await requestWithMeta(catalogPageUrl(business, 'categories', nextPage))
      if (!ok) return
      setCategories((prev) => appendUniqueById(prev, mapBusinessCategories(data?.categories)))
      categoriesPageRef.current = nextPage
      categoriesHasMoreRef.current = pageHasMore(data?.pagination)
      setCategoriesHasMore(categoriesHasMoreRef.current)
    } finally {
      categoriesLoadingRef.current = false
    }
  }, [business, requestWithMeta])

  const loadMoreProducts = useCallback(async () => {
    if (!business || productsLoadingRef.current || !productsHasMoreRef.current) return
    const requestId = productsRequestRef.current
    const nextPage = productsPageRef.current + 1
    productsLoadingRef.current = true
    try {
      const { ok, data } = await requestWithMeta(
        catalogPageUrl(business, 'products', nextPage, selectedCategoryRef.current)
      )
      if (requestId !== productsRequestRef.current || !ok) return
      setProducts((prev) => appendUniqueById(prev, mapBusinessProductGroups(data?.products)))
      productsPageRef.current = nextPage
      productsHasMoreRef.current = pageHasMore(data?.pagination)
      setProductsHasMore(productsHasMoreRef.current)
    } finally {
      if (requestId === productsRequestRef.current) productsLoadingRef.current = false
    }
  }, [business, requestWithMeta])

  useEffect(() => {
    if (loading || !productsHasMore) return
    const el = productsEndRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMoreProducts()
      },
      { rootMargin: '240px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [loading, productsHasMore, products.length, productsPending, loadMoreProducts])

  useLayoutEffect(() => {
    if (!scrollCategoryIntoViewRef.current || activeCategoryId == null) {
      categoryScrollSawPendingRef.current = false
      return
    }
    if (productsPending) categoryScrollSawPendingRef.current = true
    const target = categoryTitleRef.current || catalogBodyRef.current
    if (!target) return
    const align = () => alignElementToViewportTop(target)
    align()
    const frame = requestAnimationFrame(align)
    const timer = window.setTimeout(align, 60)
    if (categoryScrollSawPendingRef.current && !productsPending) {
      scrollCategoryIntoViewRef.current = false
      categoryScrollSawPendingRef.current = false
    }
    return () => {
      cancelAnimationFrame(frame)
      window.clearTimeout(timer)
    }
  }, [activeCategoryId, productsPending, products])

  const handleSelectCategory = (category) => {
    const id = category?.id ?? category?._id
    if (id == null) return
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
    if (activeCategoryId != null && String(activeCategoryId) === String(id)) {
      scrollCategoryIntoViewRef.current = false
      selectedCategoryRef.current = null
      setActiveCategoryId(null)
      return
    }
    scrollCategoryIntoViewRef.current = true
    selectedCategoryRef.current = id
    setActiveCategoryId(id)
  }

  const handleNavLogoClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleAddToCart = (product, buttonPosition) => {
    const productId = product?.id ?? product?._id ?? product?.productId
    const isRental = Number(businessData?.typeId) === BusinessTypes.RENTAL
    if (isRental) {
      const minutes = Number(product?.durationMinutes)
      if (!Number.isInteger(minutes) || minutes <= 0) {
        setRentalNotice('У этого товара не задана длительность аренды')
        return
      }
      setRentalNotice(null)
      setRentalPicker(product)
      return
    }

    const key = String(productId ?? product?.name ?? '')
    if (!key) return

    const promoLine =
      productHasDiscountedPrice(product) && product?.promotionId != null && product?.promotionId !== ''
        ? { promotionId: product.promotionId }
        : {}

    // Add/increment product in cart
    setCart(prev => {
      const existing = prev[key]
      if (existing) {
        return {
          ...prev,
          [key]: { ...existing, quantity: existing.quantity + 1 },
        }
      }
      const unitPrice = parsePriceVal(product?.price)
      return {
        ...prev,
        [key]: {
          key,
          id: productId ?? key,
          name: product?.name ?? 'Unknown',
          price: Number.isFinite(unitPrice) ? unitPrice : undefined,
          image: product?.image,
          quantity: 1,
          ...promoLine,
        },
      }
    })

    // Show FAB with animation from button position
    if (!fabVisible) {
      setFabPosition(buttonPosition)
      setFabVisible(true)
    }
  }

  const confirmRentalPeriod = (period) => {
    const product = rentalPicker
    if (!product) return
    const productId = product?.id ?? product?._id
    const key = rentalLineKey(productId, period.fromAt, period.toAt)
    const promoLine =
      productHasDiscountedPrice(product) && product?.promotionId != null && product?.promotionId !== ''
        ? { promotionId: product.promotionId }
        : {}
    const unitPrice = parsePriceVal(product?.price)
    setCart(prev => {
      const existing = prev[key]
      const added = Number(period.quantity) || 1
      if (existing) {
        return {
          ...prev,
          [key]: { ...existing, quantity: existing.quantity + added },
        }
      }
      return {
        ...prev,
        [key]: {
          key,
          id: productId,
          name: product?.name ?? 'Unknown',
          price: Number.isFinite(unitPrice) ? unitPrice : undefined,
          image: product?.image,
          quantity: added,
          durationMinutes: Number(product.durationMinutes),
          fromAt: period.fromAt,
          toAt: period.toAt,
          ...promoLine,
        },
      }
    })
    setRentalPicker(null)
    if (!fabVisible) setFabVisible(true)
  }

  const loadRentalAvailable = useCallback(async (fromAt, toAt) => {
    const productId = rentalPicker?.id ?? rentalPicker?._id
    const params = new URLSearchParams({
      productId: String(productId),
      fromAt,
      toAt,
    })
    const { ok, data } = await requestWithMeta(
      `/business/${encodeURIComponent(business)}/rental-availability?${params.toString()}`
    )
    if (!ok) throw new Error(data?.error || 'Не удалось проверить наличие')
    return data?.available
  }, [business, rentalPicker, requestWithMeta])

  const loadRentalOccupancy = useCallback(async (fromAt, toAt) => {
    const productId = rentalPicker?.id ?? rentalPicker?._id
    const params = new URLSearchParams({
      productId: String(productId),
      fromAt,
      toAt,
    })
    const { ok, data } = await requestWithMeta(
      `/business/${encodeURIComponent(business)}/rental-occupancy?${params.toString()}`
    )
    if (!ok) throw new Error(data?.error || 'Не удалось проверить наличие')
    return data?.steps
  }, [business, rentalPicker, requestWithMeta])

  const cartSummary = useMemo(() => Object.values(cart), [cart])
  const cartCount = useMemo(
    () => cartSummary.reduce((sum, it) => sum + (it.quantity || 0), 0),
    [cartSummary]
  )

  const handleCartIncrease = (key) => {
    setCart(prev => {
      const it = prev[key]
      if (!it) return prev
      return { ...prev, [key]: { ...it, quantity: it.quantity + 1 } }
    })
  }

  const handleCartDecrease = (key) => {
    setCart(prev => {
      const it = prev[key]
      if (!it) return prev
      if (it.quantity <= 1) {
        const next = { ...prev }
        delete next[key]
        return next
      }
      return { ...prev, [key]: { ...it, quantity: it.quantity - 1 } }
    })
  }

  const handleCartRemoveLine = (key) => {
    setCart(prev => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  useEffect(() => {
    if (cartCount === 0) {
      setFabVisible(false)
      setIsCartOpen(false)
    }
  }, [cartCount])

  useEffect(() => {
    const merged = location.state?.openCartWithMerged
    if (!merged || typeof merged !== 'object') return
    const sig = `${business}::${JSON.stringify(merged)}`
    if (giftMergeSigRef.current === sig) return
    giftMergeSigRef.current = sig

    setCart(merged)
    setFabVisible(Object.keys(merged).length > 0)
    setIsCartOpen(true)
    navigate(location.pathname, { replace: true, state: {} })
  }, [location.state, location.pathname, navigate, business])

  return (
    <div className={`customer-side${loading ? ' customer-side--loading' : ''} customer-side--view-${businessViewClass}`}>
      {loading ? (
        <div className="customer-honeycomb-wrap" role="status" aria-live="polite" aria-label="Загрузка">
          <div className="honeycomb" aria-hidden="true">
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
          </div>
        </div>
      ) : (
        <>
          {!error && (
            banners.length > 0 ? (
              <BannerHeader
                imageUrl={businessData?.logoUrl}
                banners={banners}
                businessName={businessData?.name || business}
              />
            ) : (
              <Header imageUrl={businessData?.logoUrl} />
            )
          )}
          <main className="customer-main">
            {error && (
              <div className="store-not-found-card">
                <div className="store-not-found-icon">🏪</div>
                <h2 className="store-not-found-title">Бизнес не найден</h2>
                <p className="store-not-found-message">
                  Бизнес, который вы ищите, не существует или был удален.
                </p>
                <p className="store-not-found-hint">
                  Пожалуйста, проверьте URL и попробуйте снова.
                </p>
              </div>
            )}
            {!error && (
              <>
                {showCategoryNav ? (
                  <div ref={navSentinelRef} className="category-nav-sentinel" aria-hidden="true" />
                ) : null}
                <div className={`catalog-layout${showCategoryNav ? ' catalog-layout--with-nav' : ''}`}>
                  {showCategoryNav ? (
                    <div className="category-nav-mobile-wrap">
                      <CategoryNav
                        variant="mobile"
                        categories={categories}
                        activeId={activeCategoryId}
                        logoUrl={logoUrl}
                        stuck={navStuck}
                        hasMore={categoriesHasMore}
                        onReachEnd={loadMoreCategories}
                        onSelect={handleSelectCategory}
                        onLogoClick={handleNavLogoClick}
                      />
                    </div>
                  ) : null}

                  {showCategoryNav ? (
                    <CategoryNav
                      variant="desktop"
                      categories={categories}
                      activeId={activeCategoryId}
                      logoUrl={logoUrl}
                      stuck={navStuck}
                      hasMore={categoriesHasMore}
                      onReachEnd={loadMoreCategories}
                      onSelect={handleSelectCategory}
                      onLogoClick={handleNavLogoClick}
                    />
                  ) : null}

                  <div className="catalog-body" ref={catalogBodyRef}>
                    {productsPending && products.length === 0 ? (
                      <CatalogSkeletons />
                    ) : products.length === 0 ? (
                      <p className="catalog-empty">No products available</p>
                    ) : isLinearView ? (
                      <LinearCatalog
                        products={products}
                        cartCount={cartCount}
                        onAddToCart={handleAddToCart}
                      />
                    ) : isMagazineView ? (
                      <div className="magazine-catalog">
                        {products.map((product, productIndex) => (
                          <MagazineProductSpread
                            key={product.id}
                            product={product}
                            index={productIndex}
                            reversed={productIndex % 2 === 1}
                            cartCount={cartCount}
                            onAddToCart={handleAddToCart}
                          />
                        ))}
                      </div>
                    ) : (
                      <CatalogProducts
                        products={products}
                        titleRef={categoryTitleRef}
                        categoryName={
                          activeCategoryId == null
                            ? null
                            : categories.find(
                                (category) => String(category.id ?? category._id) === String(activeCategoryId)
                              )?.name
                        }
                        cartCount={cartCount}
                        onAddToCart={handleAddToCart}
                        CardComponent={isFashionView ? FashionProductCard : ProductCard}
                      />
                    )}
                    {productsHasMore ? (
                      <div ref={productsEndRef} className="catalog-scroll-sentinel" aria-hidden="true" />
                    ) : null}
                  </div>
                </div>
              </>
            )}
          </main>
          <Footer />
          <FloatingActionButton
            count={cartCount}
            isVisible={fabVisible && cartCount > 0}
            initialPosition={fabPosition}
            onClick={() => setIsCartOpen(true)}
          />
          <CartModal
            isOpen={isCartOpen}
            items={cartSummary}
            onIncrease={handleCartIncrease}
            onDecrease={handleCartDecrease}
            onRemove={handleCartRemoveLine}
            onClose={() => setIsCartOpen(false)}
          />
          {rentalNotice ? (
            <div className="rental-catalog-notice" role="status">{rentalNotice}</div>
          ) : null}
          {rentalPicker ? (
            <RentalPeriodPicker
              key={rentalPicker.id ?? rentalPicker._id}
              productName={rentalPicker.name}
              unitPrice={Number(rentalPicker.price) || 0}
              durationMinutes={rentalPicker.durationMinutes}
              suggestions={uniquePeriodSuggestions(cartSummary)}
              allowPast={false}
              loadAvailable={loadRentalAvailable}
              loadOccupancy={loadRentalOccupancy}
              onConfirm={confirmRentalPeriod}
              onClose={() => setRentalPicker(null)}
            />
          ) : null}
        </>
      )}
    </div>
  )
}

export default CustomerSide
